import { IConfig } from '@cian/config/shared';
import { IHttpApi } from '@cian/http-api/shared';
import { TDeviceType } from '@cian/ui-kit';

import { ResponseError } from '../../errors';
import { prepareReviews } from '../../mappers/prepareReviews';
import { mapPageDataTripReviews } from './mappers/mapPageDataTripReviews';
import {
  fetchPageDataTripReviews,
  IPageDataTripReviewsRequest,
  TPageDataTripReviewsResponse,
} from '../../repositories/dailyrent/v1/page-data/trip-reviews';
import {
  fetchPublicWebV1GetReviews,
  TPublicWebV1GetReviewsResponse,
} from '../../repositories/dailyrent/web/v1/get-reviews';
import { mapDailyrentResponse } from './mappers/mapDailyrentResponse';
import { TMappedDailyrentResponse } from '../../types/reviews';
import { getCianDomain } from '../../utils/cianDomain';

interface IFetchReviewsParams {
  config: IConfig;
  httpApi: IHttpApi;
  parameters: IPageDataTripReviewsRequest;
  deviceType?: TDeviceType;
  shouldUsePageDataRequest?: boolean;
}

export async function dailyrentFetchReviews(params: IFetchReviewsParams) {
  let mappedResponse: TMappedDailyrentResponse;

  if (params.shouldUsePageDataRequest) {
    mappedResponse = await fetchAndMapPageData(params);
  } else {
    mappedResponse = await fetchAndMapGetReviews(params);
  }

  const areAnswersInitiallyOpened = params.deviceType === 'desktop';

  return {
    objectInfo: mappedResponse.objectInfo,
    moderationRulesUrl: mappedResponse.moderationRulesUrl,
    ...prepareReviews(mappedResponse, areAnswersInitiallyOpened),
  };
}

async function fetchAndMapPageData({ config, httpApi, parameters, deviceType }: IFetchReviewsParams) {
  const result = (await fetchPageDataTripReviews({
    httpApi,
    parameters,
  })) as TPageDataTripReviewsResponse;

  if (result.statusCode !== 200) {
    throw new ResponseError({
      domain: 'reviews#fetchReviews',
      message: result.response.message as string,
    });
  }

  return mapPageDataTripReviews(result.response, parameters.offerId, getCianDomain(config));
}

async function fetchAndMapGetReviews({ httpApi, parameters }: IFetchReviewsParams) {
  const { offerId } = parameters;

  const result = (await fetchPublicWebV1GetReviews({
    httpApi,
    parameters: {
      offerRealtyId: offerId,
    },
  })) as TPublicWebV1GetReviewsResponse;

  if (result.statusCode !== 200) {
    throw new ResponseError({
      domain: 'reviews#fetchReviews',
      message: result.response.message as string,
    });
  }

  return mapDailyrentResponse(result.response, offerId);
}
